import { useCallback } from 'react';
import { logger } from '@monofront/logging';

export type RetriableProps<T> = {
  /**
   * The operation that will be tried and retried.
   */
  operation: () => PromiseLike<T>;
  /**
   * Number of tries before calling `onError`. Default is 3.
   */
  numberOfTries?: number;
  /**
   * Callback if `shouldRetry` returns `false` or `numberOfTries` is exceeded.
   *
   * @param error The error if one was catched, or nothing.
   */
  onError: (error?: unknown) => void;
  /**
   * Callback if `operation` is a success.
   */
  onSuccess: (result: T) => void;
  /**
   * Should returns whether to retry the `operation` or not, depending on the
   * result, or the error. Default is to retry on any error catched.
   */
  shouldRetry?: (
    intermediateResult: { result: T } | { error: unknown },
  ) => boolean;
};

const DEFAULT_NUMBER_OF_TRIES = 3;
const DEFAULT_SHOULD_RETRY_HANDLER: RetriableProps<unknown>['shouldRetry'] = (
  intermediateResult,
) => {
  return 'error' in intermediateResult;
};

/**
 * Automatically retries an operation a number of times before calling `onError`.
 * If the provided `shouldRetry` returns `false`, or `numberOfTries` is exceeded,
 * `onError` is called.
 */
export const useRetriable = <T>({
  operation,
  numberOfTries = DEFAULT_NUMBER_OF_TRIES,
  onError,
  onSuccess,
  shouldRetry = DEFAULT_SHOULD_RETRY_HANDLER,
}: RetriableProps<T>) => {
  return useCallback(
    async function retryWrapper() {
      for (let tries = numberOfTries; tries > 0; tries--) {
        try {
          const result = await operation();

          if (!shouldRetry({ result })) {
            onSuccess(result);

            break;
          }

          if (tries === 1) {
            onError();

            break;
          }

          logger.info(
            `Failed retriable operation ${operation.name}, retrying ${
              numberOfTries - tries + 2
            }/${numberOfTries}`,
          );
        } catch (error: unknown) {
          if (tries === 1 || !shouldRetry({ error })) {
            onError(error);

            break;
          }

          logger.info(
            `Failed retriable operation ${operation.name}, retrying ${
              numberOfTries - tries + 2
            }/${numberOfTries}`,
          );
        }
      }
    },
    [operation, numberOfTries, shouldRetry],
  );
};
